import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type LanguageCode = 'en' | 'ko' | 'es';

interface Translations {
  title: string;
  subtitle: string;
  aiGreeting: string;
  aiThinking: string;
  aiActive: string;
  life: string;
  money: string;
  joy: string;
  scroll: string;
  lifeDesc?: string;
  moneyDesc?: string;
  joyDesc?: string;
  // Transformation Section
  toFlyFree: string;
  agencyRequiresTrust: string;
  highAgencyEarnsTrust: string;
  whatIsAgency: string;
  agencyDescription: string;
  autonomy: string;
  autonomyDesc: string;
  intrinsicMotivation: string;
  intrinsicMotivationDesc: string;
  competence: string;
  competenceDesc: string;
  relatedness: string;
  relatednessDesc: string;
  agencyRequiresBalance: string;
  balanceEnablesAgility: string;
  highAgencyCreates: string;
  helicopterVisualize: string;
  helicopterAgile: string;
  helicopterHover: string;
  helicopterLand: string;
  helicopterSkill: string;
  beginJourney: string;
}

const translations: Record<LanguageCode, Translations> = {
  en: {
    title: "Transform and Thrive",
    subtitle: "Agency + Kindness",
    aiGreeting: "Hello, I'm your AI guide",
    aiThinking: "Analyzing your journey...",
    aiActive: "Let's explore together",
    life: "LIFE Stories",
    money: "MONEY Balance",
    joy: "JOY Connections",
    scroll: "What is Human Agency?",
    lifeDesc: "Build your foundation",
    moneyDesc: "Power your journey",
    joyDesc: "Bending Space - Time",
    // Transformation Section
    toFlyFree: "To fly free, you need Agency.",
    agencyRequiresTrust: "Agency requires Trust in yourself.",
    highAgencyEarnsTrust: "High Agency people earn the trust of others.",
    whatIsAgency: "What is Agency",
    agencyDescription: "Agency is both a state of mind and a set of behaviors. Those with agency lead their lives (and others) with a belief that their decisions and actions impact future outcomes. It requires that you pilot your own life. It is the highest form of leadership and it is innately yours. You cannot lead others if you cannot lead yourself.",
    autonomy: "Autonomy",
    autonomyDesc: "Self-direction and independence in your choices",
    intrinsicMotivation: "Intrinsic Motivation",
    intrinsicMotivationDesc: "Internal drive and passion for what you do",
    competence: "Competence",
    competenceDesc: "Understanding of the key principles underlying systems",
    relatedness: "Relatedness",
    relatednessDesc: "Meaningful connections and relationships",
    agencyRequiresBalance: "Agency Requires Balance",
    balanceEnablesAgility: "Balance enables Agility",
    highAgencyCreates: "High agency creates a fluid mastery of the dynamics connecting life, money, and joy. At home and at work.",
    helicopterVisualize: "A helicopter is a good way to visualize Agency.",
    helicopterAgile: "It's very agile and can fly in six directions",
    helicopterHover: "It can hover in mid-air",
    helicopterLand: "It can land and take-off virtually anywhere",
    helicopterSkill: "It requires both skill and feel",
    beginJourney: "Begin Your Journey"
  },
  ko: {
    title: "변화와 번창",
    subtitle: "에이전시 + 친절",
    aiGreeting: "안녕하세요, AI 가이드입니다",
    aiThinking: "여정을 분석하는 중...",
    aiActive: "함께 탐험해요",
    life: "LIFE 이야기",
    money: "MONEY 균형",
    joy: "JOY 연결",
    scroll: "인간 주체성이란 무엇인가?",
    lifeDesc: "기초를 다지세요",
    moneyDesc: "여정에 힘을 실어주세요",
    joyDesc: "시공간 구부리기",
    // Transformation Section
    toFlyFree: "자유롭게 날기 위해서는 자기 주도성이 필요합니다.",
    agencyRequiresTrust: "자기 주도성은 자신에 대한 신뢰가 필요합니다.",
    highAgencyEarnsTrust: "높은 자기 주도성을 가진 사람들은 다른 사람들의 신뢰를 얻습니다.",
    whatIsAgency: "자기 주도성이란 무엇인가",
    agencyDescription: "자기 주도성은 마음의 상태이자 행동의 집합입니다. 자기 주도성을 가진 사람들은 자신의 결정과 행동이 미래의 결과에 영향을 미친다는 믿음으로 자신과 타인의 삶을 이끕니다. 자신의 삶을 조종해야 합니다. 이것은 리더십의 최고 형태이며 본질적으로 당신의 것입니다. 자신을 이끌 수 없다면 다른 사람을 이끌 수 없습니다.",
    autonomy: "자율성",
    autonomyDesc: "선택에서의 자기 주도와 독립성",
    intrinsicMotivation: "내재적 동기",
    intrinsicMotivationDesc: "하는 일에 대한 내적 추진력과 열정",
    competence: "유능감",
    competenceDesc: "시스템의 핵심 원리에 대한 이해",
    relatedness: "관계성",
    relatednessDesc: "의미 있는 연결과 관계",
    agencyRequiresBalance: "자기 주도성은 균형이 필요합니다",
    balanceEnablesAgility: "균형은 민첩성을 가능하게 합니다",
    highAgencyCreates: "높은 자기 주도성은 집과 직장에서 삶, 돈, 기쁨을 연결하는 역학의 유연한 숙달을 만들어냅니다.",
    helicopterVisualize: "헬리콥터는 자기 주도성을 시각화하는 좋은 방법입니다.",
    helicopterAgile: "매우 민첩하며 6개 방향으로 날 수 있습니다",
    helicopterHover: "공중에서 정지할 수 있습니다",
    helicopterLand: "거의 어디든 착륙하고 이륙할 수 있습니다",
    helicopterSkill: "기술과 감각이 모두 필요합니다",
    beginJourney: "여정 시작하기"
  },
  es: {
    title: "Transforma y Prospera",
    subtitle: "Agencia + Bondad",
    aiGreeting: "Hola, soy tu guía de IA",
    aiThinking: "Analizando tu viaje...",
    aiActive: "Exploremos juntos",
    life: "Historias de VIDA",
    money: "Balance de DINERO",
    joy: "Conexiones de ALEGRÍA",
    scroll: "¿Qué es la Agencia Humana?",
    lifeDesc: "Construye tu fundación",
    moneyDesc: "Impulsa tu viaje",
    joyDesc: "Doblando el Espacio-Tiempo",
    // Transformation Section
    toFlyFree: "Para volar libre, necesitas Agencia.",
    agencyRequiresTrust: "La Agencia requiere Confianza en ti mismo.",
    highAgencyEarnsTrust: "Las personas con alta Agencia ganan la confianza de otros.",
    whatIsAgency: "¿Qué es la Agencia?",
    agencyDescription: "La agencia es tanto un estado mental como un conjunto de comportamientos. Aquellos con agencia lideran sus vidas (y las de otros) con la creencia de que sus decisiones y acciones impactan los resultados futuros. Requiere que pilotees tu propia vida. Es la forma más alta de liderazgo y es intrínsecamente tuya. No puedes liderar a otros si no puedes liderarte a ti mismo.",
    autonomy: "Autonomía",
    autonomyDesc: "Autodirección e independencia en tus elecciones",
    intrinsicMotivation: "Motivación Intrínseca",
    intrinsicMotivationDesc: "Impulso interno y pasión por lo que haces",
    competence: "Competencia",
    competenceDesc: "Comprensión de los principios clave de los sistemas",
    relatedness: "Relacionalidad",
    relatednessDesc: "Conexiones y relaciones significativas",
    agencyRequiresBalance: "La Agencia Requiere Equilibrio",
    balanceEnablesAgility: "El equilibrio permite la agilidad",
    highAgencyCreates: "La alta agencia crea un dominio fluido de las dinámicas que conectan la vida, el dinero y la alegría. En casa y en el trabajo.",
    helicopterVisualize: "Un helicóptero es una buena forma de visualizar la Agencia.",
    helicopterAgile: "Es muy ágil y puede volar en seis direcciones",
    helicopterHover: "Puede flotar en el aire",
    helicopterLand: "Puede aterrizar y despegar virtualmente en cualquier lugar",
    helicopterSkill: "Requiere tanto habilidad como sensibilidad",
    beginJourney: "Comienza tu Viaje"
  }
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem('preferred-language');
    return (saved as LanguageCode) || 'en';
  });

  useEffect(() => {
    localStorage.setItem('preferred-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const value = {
    language,
    setLanguage,
    t: translations[language]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};